import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { dispatchApi } from "@/lib/tourEntities";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { SectionHeader } from "@/components/SectionHeader";
import { RoleGate } from "@/components/RoleGate";
import { RefreshCw } from "lucide-react";

const READ = ["super_admin", "leasing_sales", "ops_dispatch", "accounting", "maintenance"];

const BUCKET_LABEL = {
  missing_coach: "Missing Coach",
  missing_driver: "Missing Driver",
  pickup_within_72h_incomplete: "Pickup <72h — incomplete",
  coach_in_maintenance: "Coach in Maintenance",
  driver_credential_warning: "Driver Credential Warning",
  contract_not_executed: "Contract Not Executed",
  pickup_sheet_not_generated: "Pickup Sheet Not Generated",
};

export default function DispatchPage() {
  const [data, setData] = useState(null);
  const load = async () => {
    try { setData(await dispatchApi.queue()); } catch { toast.error("Failed to load dispatch"); }
  };
  useEffect(() => { load(); }, []);

  return (
    <RoleGate allow={READ}>
      <PageShell>
        <PageHeader testid="page-dispatch" title="Dispatch Queue"
          subtitle="Tours needing operational action"
          right={
            <button data-testid="dispatch-refresh" onClick={load}
              className="inline-flex items-center gap-1.5 border border-coach-blue text-coach-blue text-[12.5px] px-3 py-1.5 rounded-sm">
              <RefreshCw size={14} /> Refresh
            </button>
          }
        />
        {!data ? <div className="text-steel text-sm">Loading…</div> :
          Object.entries(BUCKET_LABEL).map(([k, label]) => {
            const rows = data[k] || [];
            if (rows.length === 0) return null;
            return (
              <div key={k} className="bg-white border border-coach-silver rounded-md mb-4" data-testid={`dispatch-bucket-${k}`}>
                {/* Correction batch — Dispatch section header
                    readability: font-size ~10% up (10.5→11.5px),
                    weight 400→600, contrast strengthened
                    (text-steel #6B747C on road-white #F7F8F9 ~3.9:1
                    → text-carbon #111315 on road-white ~15.8:1 light
                    mode, well above WCAG AA 4.5:1 for regular text
                    at this weight). Row height + cell padding
                    unchanged; body table density preserved. */}
                <div className="px-4 py-2 bg-road-white border-b border-coach-silver">
                  {/* RC4 · Dispatch section header converged onto the
                      shared SectionHeader (visual token identical to
                      the RC1 step-5 improvement — 11.5px uppercase
                      font-semibold text-carbon on road-white). */}
                  <SectionHeader
                    testid={`dispatch-section-header-${label.toLowerCase().replaceAll(" ", "-")}`}
                    className="mb-0"
                  >
                    {label} · {rows.length}
                  </SectionHeader>
                </div>
                <table className="hidden md:table w-full text-[13px]">
                  <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
                    <tr>
                      <th className="text-left px-3 py-1.5 w-28">Tour #</th>
                      <th className="text-left px-3 py-1.5">Tour</th>
                      <th className="text-left px-3 py-1.5">Issue</th>
                      <th className="text-left px-3 py-1.5 w-40">Dates</th>
                      <th className="w-24" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.tour_id + i} data-testid={`dispatch-row-${r.tour_number}`} className="border-t border-coach-silver/60">
                        <td className="px-3 py-1.5 font-mono text-[12px] text-steel">{r.tour_number}</td>
                        <td className="px-3 py-1.5">{r.display_name}</td>
                        <td className="px-3 py-1.5 text-carbon">{r.issue}</td>
                        <td className="px-3 py-1.5 text-steel">{r.start_date} → {r.end_date}</td>
                        <td className="px-3 py-1.5 text-right">
                          <Link to={`/tours/${r.tour_id}`} className="text-coach-blue hover:underline text-[12px]">Open</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-coach-silver/60">
                  {rows.map((r, i) => (
                    <Link key={r.tour_id + i} to={`/tours/${r.tour_id}`}
                      data-testid={`dispatch-card-${r.tour_number}`}
                      className="flex items-start gap-3 p-3 hover:bg-road-white">
                      <div className="flex-1 min-w-0">
                        <div className="text-[10.5px] font-mono uppercase tracking-widest text-steel">{r.tour_number}</div>
                        <div className="text-[15px] font-semibold text-carbon leading-tight break-words">{r.display_name}</div>
                        <div className="text-[13px] text-carbon mt-1">{r.issue}</div>
                        <div className="text-[11.5px] text-steel mt-1">{r.start_date} → {r.end_date}</div>
                      </div>
                      <span className="text-[11px] uppercase tracking-widest text-amber-hold font-semibold shrink-0">ACTION</span>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })
        }
        {data && Object.values(data).every((v) => (v || []).length === 0) && (
          <div className="bg-white border border-coach-silver rounded-md p-8 text-center text-steel text-[13px]" data-testid="dispatch-empty">
            All clear. No actionable items.
          </div>
        )}
      </PageShell>
    </RoleGate>
  );
}
